// src/components/home/Hero/Hero.jsx
import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import "./Hero.css";
import visit from "../../utils/visit";
import { useNavigate } from "react-router-dom";

const PROPERTY_TYPES = ["Any", "House", "Apartment", "Plot", "Commercial"];

const PRICE_RANGES = [
  { label: "Any", value: "any" },
  { label: "Under 10M", value: "under_10m" },
  { label: "10M - 25M", value: "10m_25m" },
  { label: "25M - 50M", value: "25m_50m" },
  { label: "50M+", value: "50m_plus" },
];

export default function Hero({ onSearch }) {
  const [location, setLocation] = useState("");
  const [type, setType] = useState("Any");
  const [price, setPrice] = useState("any");

  const stats = useMemo(
    () => [
      { label: "Verified Listings", value: "5,200+" },
      { label: "Happy Clients", value: "1,800+" },
      { label: "Cities Covered", value: "12+" },
    ],
    []
  );

  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();

    const params = new URLSearchParams();
    if (location.trim()) params.set('location', location.trim());
    if (type && type !== 'Any') params.set('type', type === 'House' ? 'Residential' : type);

    const priceMap = {
      under_10m: ['0', '10000000'],
      '10m_25m': ['10000000', '25000000'],
      '25m_50m': ['25000000', '50000000'],
      '50m_plus': ['50000000', ''],
    };

    if (priceMap[price]) {
      const [min, max] = priceMap[price];
      if (min) params.set('minPrice', min);
      if (max) params.set('maxPrice', max);
    }

    const payload = { location: location.trim(), type, price };
    if (typeof onSearch === 'function') onSearch(payload);
    navigate(`/listing${params.toString() ? `?${params.toString()}` : ''}`);
  };
  return (
    <section className="rs-hero">
      <div className="rs-hero__bg" aria-hidden="true" />
      <div className="rs-hero__grid" aria-hidden="true" />

      <div className="container rs-hero__container">
        <div className="row align-items-center g-4">
          {/* Left Content */}
          <div className="col-12 col-lg-7">
            <motion.div
              className="rs-hero__content"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <span className="rs-hero__badge">
                Premium Real Estate • Buy • Rent • Invest
              </span>

              <h1 className="rs-hero__title">
                Find Your <span className="rs-hero__accent">Perfect</span>{" "}
                Property — Fast, Safe, and Verified.
              </h1>

              <p className="rs-hero__subtitle">
                Browse modern homes, apartments, plots, and commercial spaces.
                Smart filters, verified listings, and expert agents — all in one
                place.
              </p>

              <div className="rs-hero__cta">
                <button
                  onClick={() => navigate("/listing")}
                  className="rs-btn rs-btn--primary"
                  type="button"
                >
                  Explore Listings
                </button>
                <a href={visit()} target="_blank">
                  <button className="rs-btn rs-btn--ghost" type="button">
                    Talk to an Agent
                  </button>
                </a>
              </div>

              <div className="rs-hero__stats">
                {stats.map((s) => (
                  <div key={s.label} className="rs-stat">
                    <div className="rs-stat__value">{s.value}</div>
                    <div className="rs-stat__label">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Search Card */}
          <div className="col-12 col-lg-5">
            <motion.div
              className="rs-hero__card"
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, ease: "easeOut", delay: 0.05 }}
            >
              <div className="rs-hero__cardHead">
                <div>
                  <h3 className="rs-hero__cardTitle">Quick Search</h3>
                  <p className="rs-hero__cardSub">
                    Start with location and filters
                  </p>
                </div>
                <div className="rs-hero__pill">Verified</div>
              </div>

              <form onSubmit={handleSubmit} className="rs-form">
                <div className="rs-field">
                  <label className="rs-label">Location</label>
                  <input
                    className="rs-input"
                    type="text"
                    placeholder="e.g., DHA, Bahria, Gulberg..."
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </div>

                <div className="row g-3">
                  <div className="col-12 col-sm-6">
                    <div className="rs-field">
                      <label className="rs-label">Property Type</label>
                      <select
                        className="rs-input"
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                      >
                        {PROPERTY_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6">
                    <div className="rs-field">
                      <label className="rs-label">Price Range</label>
                      <select
                        className="rs-input"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                      >
                        {PRICE_RANGES.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <button
                  className="rs-btn rs-btn--primary rs-btn--full"
                  type="submit"
                >
                  Search Properties
                </button>

                <div className="rs-hero__hint">
                  Tip: You can refine results on the Listings page.
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
